import {
  ConfigurableCartItem,
  ConfigurableProduct,
  Maybe,
  ProductImage,
  ProductStockStatus,
  useAddToCartMutation,
  useCartPageQuery,
  useFetchCartHeaderQuery,
  usePdpQuery,
  useRemoveItemFromCartMutation,
} from '@magentoTypes'
import { Image } from '@next/image'
import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { createMagentoClient } from '~/graphql/magentoClient'
import { animated, useSpring } from '@react-spring/web'
import { toast } from 'react-toastify'
import { getCartId, useCartId } from '~/hooks/useCartId'
import { useRouter } from 'next/router'
import clsx from 'clsx'
import PulseLoader from 'react-spinners/PulseLoader'
import { Link } from '~/elements/Link/Link'
import { getSession } from 'next-auth/react'
import { CART_ID, DEFAULT_LOCALE } from '~/config/constants'
import { parseSampleBoxCategory } from '~/modules/PLP/lib/parseSampleBoxCategory'
import { ButtonMain } from '~/elements/Button/ButtonMain'
import { createLanguageRegionLocale } from '~/lib/createLanguageRegionLocale'

type Props = {
  closeFlyout: () => void
  showSampleBoxSelector: boolean
  urlKey?: string
}
type SampleItem = {
  state?: ItemState
  stock_status?: Maybe<ProductStockStatus>
  sku?: Maybe<string>
  parent_sku?: Maybe<string>
  url_key?: Maybe<string>
  uid: Maybe<string>
  small_image?: Maybe<ProductImage>
}
enum ItemState {
  READY,
  LOADING,
  PICKED,
  DELETE,
}
const samplesValueIndex = [
  11932, // 15x15cm - Live/Staging
  10771, // 10x10cm - Live/Staging
]

export type SamplesNotification = {
  cartId: string
  samples: SampleItem[]
}

export const SampleBoxSelector = ({ closeFlyout, showSampleBoxSelector, urlKey }: Props) => {
  const SAMPLE_BOX_ITEMS_THRESHOLD = 5

  const router = useRouter()
  const finalLocale = router.locale ? createLanguageRegionLocale(router.locale) : DEFAULT_LOCALE
  const [cartId] = useCartId()
  const queryClient = useQueryClient()
  const magentoClient = createMagentoClient({ usePost: true, queryClient })
  const { isFetched: isPdpFetched, data: pdpData } = usePdpQuery(
    magentoClient,
    { urlKey: urlKey || '' },
    { staleTime: 0, enabled: !!urlKey },
  )
  const { isFetched: isCartFetched, data: cartData } = useCartPageQuery(
    magentoClient,
    { cartId },
    { enabled: !!cartId },
  )
  const addToCartMutation = useAddToCartMutation(magentoClient, {
    onError: async () => {
      await queryClient.invalidateQueries([CART_ID])
      await queryClient.invalidateQueries(['session'])
    },
  })
  const removeItemFromCartMutation = useRemoveItemFromCartMutation(magentoClient)
  const [hasScrolled, setHasScrolled] = useState(false)
  const { t } = useTranslation()
  const bgSpring = useSpring({
    backgroundColor: 'rgba(20,20,20,0.4)',
  })

  const [samples, setSamples] = useState<SampleItem[]>([])
  const __samples = samples

  const addOrUpdateSample = (item: SampleItem, newState: ItemState, uid?: string) => {
    // Adds or update the list
    const itemIndex = __samples.findIndex((v) => v.sku == item.sku)
    if (newState == ItemState.DELETE) {
      if (itemIndex != -1) __samples.splice(itemIndex, 1)
    } else if (uid) {
      __samples.splice(itemIndex != -1 ? itemIndex : __samples.length, 1, {
        ...item,
        state: newState,
        uid,
      })
    } else {
      __samples.splice(itemIndex != -1 ? itemIndex : __samples.length, 1, {
        ...item,
        state: newState,
      })
    }
    // Broadcast the event
    if (typeof window != 'undefined')
      window.localStorage.setItem(
        'addOrUpdateSample',
        JSON.stringify({ cartId, samples: __samples } as SamplesNotification),
      )
    // Sync with state
    setSamples(__samples)
  }
  const addToCart = async (item: SampleItem) => {
    const itemIndex = samples.findIndex((v) => v.sku == item.sku)
    if (
      (itemIndex == -1 && item.state == ItemState.READY) ||
      (itemIndex != -1 && samples[itemIndex]?.state == ItemState.READY)
    ) {
      const samplesCount = samples.filter((v) => v.state != ItemState.READY).length
      if (samplesCount >= SAMPLE_BOX_ITEMS_THRESHOLD) {
        toast.error(
          t('Your sample box is full. Only {{items}} items are allowed', {
            items: SAMPLE_BOX_ITEMS_THRESHOLD,
          }),
        )
      } else {
        const session = await getSession()
        // First update the status
        addOrUpdateSample(item, ItemState.LOADING)
        // Run the mutation
        addToCartMutation
          .mutateAsync({
            cartId: session ? (cartId as string) : (getCartId() as string),
            cartItems: [
              {
                quantity: 1,
                sku: item?.parent_sku as string,
                selected_options: [item?.uid],
              },
            ],
          })
          .then(async (data) => {
            if (data?.addProductsToCart?.user_errors?.length || 0 > 0) {
              // Inform user from the error
              toast.error(data?.addProductsToCart?.user_errors?.[0]?.message)
            } else {
              // Update the status again
              const updatedProduct = data.addProductsToCart?.cart.items?.find(
                (v) => v?.product.sku == item.parent_sku,
              )
              if (updatedProduct) addOrUpdateSample(item, ItemState.PICKED, updatedProduct.uid)
              else addOrUpdateSample(item, ItemState.PICKED)
              // Flush query caches
              await queryClient.refetchQueries(useCartPageQuery.getKey({ cartId }))
              await queryClient.invalidateQueries(useFetchCartHeaderQuery.getKey({ cartId }))
            }
          })
          .catch(async (error) => {
            // Flush query caches
            await queryClient.refetchQueries(useCartPageQuery.getKey({ cartId }))
            await queryClient.invalidateQueries(useFetchCartHeaderQuery.getKey({ cartId }))
            // Inform user from the error
            toast.error(error?.message)
            // Update the status again
            addOrUpdateSample(item, ItemState.PICKED)
          })
      }
    }
  }
  const removeFromCart = (item: SampleItem) => {
    if (item.state == ItemState.PICKED) {
      // First update the status
      addOrUpdateSample(item, ItemState.LOADING)
      // Run the mutation
      removeItemFromCartMutation
        .mutateAsync({
          cartId,
          cartItemUid: item?.uid as string,
        })
        .then(async () => {
          // Update the status again
          addOrUpdateSample(item, ItemState.DELETE)
          // Flush query caches
          await queryClient.refetchQueries(useCartPageQuery.getKey({ cartId }))
          await queryClient.invalidateQueries(useFetchCartHeaderQuery.getKey({ cartId }))
        })
        .catch((error) => {
          // Inform user from the error
          // console.error(error)
          // toast.error(t('Error removing item from cart'))
          // Update the status again
          addOrUpdateSample(item, ItemState.DELETE)
        })
    }
  }

  // Load all samples of a same shape
  const product = pdpData?.products?.items?.[0] as ConfigurableProduct
  const products: SampleItem[] = []
  pdpData?.products?.items?.map((baseProduct) =>
    (baseProduct as ConfigurableProduct)?.variants?.map((product) => {
      const attributes = product?.attributes?.[0]

      if (
        attributes?.code == 'size' &&
        samplesValueIndex.includes(attributes?.value_index as number)
      ) {
        if (products.findIndex((v) => v.sku == product?.product?.sku) == -1) {
          products.push({
            state: ItemState.READY,
            uid: attributes?.uid,
            sku: product?.product?.sku,
            parent_sku: baseProduct?.sku,
            url_key: product?.product?.url_key,
            stock_status: product?.product?.stock_status,
            small_image: baseProduct?.small_image,
          })
        }
      }
    }),
  )
  product?.related_products?.map((relatedProduct) => {
    if (product?.benuta_form_new == relatedProduct?.benuta_form_new) {
      ;(relatedProduct as ConfigurableProduct)?.variants?.map((product) => {
        const attributes = product?.attributes?.[0]

        if (
          attributes?.code == 'size' &&
          samplesValueIndex.includes(attributes?.value_index as number)
        ) {
          if (products.findIndex((v) => v.sku == product?.product?.sku) == -1) {
            products.push({
              state: ItemState.READY,
              uid: attributes?.uid,
              sku: product?.product?.sku,
              parent_sku: relatedProduct?.sku,
              url_key: product?.product?.url_key,
              stock_status: product?.product?.stock_status,
              small_image: relatedProduct?.small_image,
            })
          }
        }
      })
    }
  })

  useEffect(() => {
    const __samples = [] as SampleItem[]
    ;(cartData?.cart?.items as ConfigurableCartItem[])?.map((item) => {
      const sku = item?.configured_variant?.sku
      if (samplesValueIndex.includes(item?.configurable_options?.[0]?.value_id as number)) {
        __samples.push({
          state: ItemState.PICKED,
          stock_status: item?.product?.stock_status,
          parent_sku: item?.product?.sku,
          url_key: item?.product?.url_key,
          small_image: item?.product?.image,
          uid: item?.uid,
          sku,
        })
      }
    })
    setSamples([...__samples])
  }, [isCartFetched])

  useEffect(() => {
    window.addEventListener('storage', (event) => {
      if (event.key == 'addOrUpdateSample') {
        const notification = JSON.parse(event.newValue as string) as SamplesNotification
        setSamples(notification.samples)
      }
    })
  }, [])

  const samplesCount = samples.filter((v) => v.state != ItemState.READY).length

  return (
    <div className="grid w-full grid-cols-1 lg:grid-cols-[1fr_550px]">
      <animated.div
        className="cursor-pointer"
        style={{ ...bgSpring, visibility: showSampleBoxSelector ? 'visible' : 'hidden' }}
        onClick={() => {
          closeFlyout()
        }}
      />
      <div className="vh100 relative grid grid-cols-1 grid-rows-[1fr_auto] bg-primary-white lg:w-[550px]">
        <div
          onScroll={(e) => setHasScrolled((e.target as Element).scrollTop > 0)}
          className="relative h-full overflow-auto"
        >
          <div
            className={clsx(
              'pointer-events-none fixed top-[0px] right-0 z-50 h-[72px] w-full bg-transparent lg:h-[104px] lg:w-[550px]',
              hasScrolled && 'shadow-[0px_4px_24px_rgba(0,0,0,0.15)]',
            )}
          />
          <p className="sticky top-0 z-40  bg-primary-white py-6 px-4 text-h4-mobile font-medium lg:py-[38px] lg:px-8 lg:text-h4-desktop">
            {t(
              (isPdpFetched || !urlKey) && isCartFetched ? 'Free fabric samples' : 'Please wait...',
            )}
          </p>
          {(isPdpFetched || !urlKey) && isCartFetched && (
            <>
              <div className="px-4 lg:px-8">
                <p
                  className="-mx-4 bg-primary-white px-4 text-p-small font-light lg:-mx-8 lg:px-8 lg:pt-[2px] lg:text-p"
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'Order 5 samples in your sample box and make the right choice of material, colour and feel. This way you also avoid returns and help us to protect the environment.',
                    ),
                  }}
                ></p>
                {urlKey &&
                  products.findIndex((item) => item?.stock_status !== 'OUT_OF_STOCK') != -1 && (
                    <div className="pt-6 lg:pt-[30px]">
                      <p className="lg:text-h5-desktop bg-primary-white text-p">
                        {t('Select a sample with a click')}
                      </p>
                      <menu className="mt-2 grid grid-cols-5 justify-items-center gap-x-3 gap-y-3 lg:grid-cols-7 lg:gap-x-4 lg:gap-y-6">
                        {products.map((item) => {
                          const isSoldOut = item?.stock_status === 'OUT_OF_STOCK'
                          const selectedIndex = samples.findIndex((v) => v.sku == item.sku)
                          return (
                            !isSoldOut && (
                              <li key={item?.sku}>
                                <label
                                  className={clsx('cursor-pointer', {
                                    '!cursor-default':
                                      selectedIndex != -1 &&
                                      samples[selectedIndex].state == ItemState.PICKED,
                                  })}
                                >
                                  <input
                                    className="peer sr-only"
                                    type="checkbox"
                                    value={item?.sku as string}
                                    onChange={() => {
                                      addToCart(item)
                                    }}
                                  />

                                  <div className="flex items-start">
                                    <div className="relative mt-1 h-[60px] w-[48px]">
                                      <Image
                                        src={item.small_image?.url as string}
                                        alt={item?.small_image?.label as string}
                                        style={{ objectFit: 'contain', objectPosition: 'center' }}
                                        sizes="150px"
                                        fill
                                      />
                                    </div>
                                    {selectedIndex != -1 &&
                                      samples[selectedIndex].state != ItemState.READY && (
                                        <div className="z-10 -ml-4">
                                          <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <rect width="16" height="16" rx="8" fill="#141414" />
                                            <path
                                              d="M4.33337 8.45834L6.62504 10.75L11.6667 5.70834"
                                              stroke="white"
                                              strokeWidth="1.3"
                                              strokeLinecap="round"
                                            />
                                          </svg>
                                        </div>
                                      )}
                                  </div>
                                </label>
                              </li>
                            )
                          )
                        })}
                      </menu>
                    </div>
                  )}
              </div>
              <div className="mt-12 border-t border-[#CACACA] pt-7">
                <div className="px-4 lg:px-8">
                  <p className="lg:text-h5-desktop bg-primary-white text-p font-bold">
                    {t('Your Sample-Box selection ({{items}}/{{max}})', {
                      items: samplesCount,
                      max: SAMPLE_BOX_ITEMS_THRESHOLD,
                    })}
                  </p>
                  <div className="flex w-full items-start">
                    <menu
                      className={clsx(
                        'mt-6 w-full border border-primary-black',
                        samplesCount < SAMPLE_BOX_ITEMS_THRESHOLD ? 'px-6 pt-6 pb-4' : 'p-6',
                      )}
                    >
                      <div className="grid w-full grid-cols-5 justify-items-center gap-x-6 gap-y-6">
                        {Array.from({ length: SAMPLE_BOX_ITEMS_THRESHOLD }, (item, index) => (
                          <div key={index} className="mr-1 flex items-start">
                            <div className="relative h-[48px] w-[48px] rounded-full bg-grey-light" />
                          </div>
                        ))}
                      </div>
                      <div className="-mt-[48px] grid w-full grid-cols-5 justify-items-center gap-x-6 gap-y-6">
                        {samples.map((item, index) => {
                          return (
                            <li key={index}>
                              <label className="cursor-pointer">
                                <input
                                  className="peer sr-only"
                                  type="checkbox"
                                  value={item?.sku as string}
                                  onChange={() => {
                                    removeFromCart(item)
                                  }}
                                />

                                <div className="flex items-start">
                                  <div className="relative h-[48px] w-[48px] rounded-full bg-grey-light">
                                    <Image
                                      className={clsx('rounded-full', {
                                        'opacity-50': item.state == ItemState.LOADING,
                                      })}
                                      src={item?.small_image?.url as string}
                                      alt={item?.small_image?.label as string}
                                      style={{ objectFit: 'none', objectPosition: 'center' }}
                                      sizes="150px"
                                      fill
                                    />
                                    <div className="flex h-full items-center justify-center">
                                      <PulseLoader
                                        loading={item.state == ItemState.LOADING}
                                        size={6}
                                        color={'#141414'}
                                      />
                                    </div>
                                  </div>

                                  <div
                                    className={clsx('z-10 -ml-3', {
                                      'opacity-0': item.state == ItemState.LOADING,
                                    })}
                                  >
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 16 16"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <rect width="16" height="16" rx="8" fill="#141414" />
                                      <path
                                        d="M11.2217 10.387C11.4565 10.6217 11.4565 10.987 11.2217 11.2217C11.1043 11.3391 10.9609 11.3913 10.8043 11.3913C10.6478 11.3913 10.5043 11.3391 10.387 11.2217L8 8.83478L5.61304 11.2217C5.49565 11.3391 5.35217 11.3913 5.19565 11.3913C5.03913 11.3913 4.89565 11.3391 4.77826 11.2217C4.54348 10.987 4.54348 10.6217 4.77826 10.387L7.16522 8L4.77826 5.61304C4.54348 5.37826 4.54348 5.01304 4.77826 4.77826C5.01304 4.54348 5.37826 4.54348 5.61304 4.77826L8 7.16522L10.387 4.77826C10.6217 4.54348 10.987 4.54348 11.2217 4.77826C11.4565 5.01304 11.4565 5.37826 11.2217 5.61304L8.83478 8L11.2217 10.387Z"
                                        fill="white"
                                      />
                                    </svg>
                                  </div>
                                </div>
                              </label>
                            </li>
                          )
                        })}
                      </div>
                      {samplesCount < SAMPLE_BOX_ITEMS_THRESHOLD && (
                        <div
                          className={clsx('mt-6 w-full text-center', {
                            'mt-[72px]': samplesCount === 0,
                          })}
                        >
                          <p className="bg-primary-white px-4 text-p-small font-light lg:px-8 lg:pt-[2px] lg:text-p">
                            {t('You still have room in your sample box?')}
                          </p>
                          {!urlKey ? (
                            <Link
                              onClick={() => closeFlyout()}
                              textType="Medium"
                              className="mt-2 block cursor-pointer"
                              isTextMediumBold
                            >
                              {t('Discover more patterns')}
                            </Link>
                          ) : (
                            <Link
                              href={`/${parseSampleBoxCategory({ locale: finalLocale })}`}
                              textType="Medium"
                              className="mt-2 block"
                              isTextMediumBold
                            >
                              {t('Discover more patterns')}
                            </Link>
                          )}
                        </div>
                      )}
                    </menu>
                    <div className="mt-1 -ml-[4.4rem]">
                      <svg
                        width="40"
                        height="33"
                        viewBox="0 0 40 33"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect width="40" height="33" fill="white" />
                        <path
                          d="M20.0617 10.8076L32.5096 9.34923C33.2387 9.24506 33.9679 9.66173 34.2283 10.3388L35.895 14.2451C36.4158 15.443 35.7387 16.7971 34.4367 17.0576L26.1554 18.9846C25.1658 19.2451 24.1762 18.8284 23.5512 17.9951L20.0617 13.0471L16.52 17.9951C15.895 18.8284 14.9054 19.2451 13.9158 18.9846L5.63457 17.0576C4.33249 16.7971 3.6554 15.443 4.17624 14.2451L5.8429 10.3388C6.10332 9.66173 6.83249 9.24506 7.56165 9.34923L20.0617 10.8076ZM5.68665 14.8701C5.58249 15.1305 5.73874 15.3909 5.99915 15.443L14.2804 17.3701C14.5929 17.4742 14.9575 17.318 15.1658 17.0576L18.5512 12.318L7.35332 11.0159L5.68665 14.8701ZM24.9054 17.0576C25.1137 17.318 25.4783 17.4742 25.7908 17.3701L34.0721 15.443C34.3325 15.3909 34.4887 15.1305 34.3846 14.8701L32.7179 11.0159L21.52 12.318L24.9054 17.0576ZM31.7283 19.0888L33.395 18.6201V26.4846C33.395 27.6305 32.6137 28.6201 31.4679 28.9326L20.6346 31.6409C20.27 31.693 19.8533 31.693 19.4367 31.6409L8.60332 28.9326C7.45749 28.6201 6.72832 27.6305 6.72832 26.4846V18.6201L8.39499 19.0888V26.4846C8.39499 26.8492 8.60332 27.2138 9.01999 27.2659L19.2283 29.818V17.5784C19.2283 17.1617 19.5929 16.7451 20.0617 16.7451C20.4783 16.7451 20.895 17.1617 20.895 17.5784V29.818L31.0512 27.2659C31.4679 27.2138 31.7283 26.8492 31.7283 26.4846V19.0888Z"
                          fill="#141414"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="mt-6 lg:mt-10">
                    <ButtonMain
                      disabled={samplesCount < SAMPLE_BOX_ITEMS_THRESHOLD}
                      fullWidth
                      onClick={() => {
                        closeFlyout()
                        router.push('/cart')
                      }}
                    >
                      {t('To the shopping cart')}
                    </ButtonMain>
                    <div className="justify-align-center mt-4 mb-8 grid items-center">
                      <ButtonMain
                        variant="Text"
                        onClick={() => {
                          closeFlyout()
                        }}
                      >
                        {t('Continue shopping samples')}
                      </ButtonMain>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
